import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../hooks/useAuth';

// Validation schema
const loginSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
  password: z.string().min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
  remember: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  });
  
  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await login({
        email: data.email,
        password: data.password,
      });
      
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Đăng nhập thất bại. Vui lòng thử lại.');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi. Vui lòng thử lại sau.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
      <div className="container-full py-8 max-w-md mx-auto">
        <div className="bg-gray-900 p-8 rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold text-center mb-6">Đăng nhập</h1>
          
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-white px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="w-full px-3 py-2 bg-gray-800 rounded border border-gray-700 focus:outline-none focus:border-red-custom"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">
                Mật khẩu
              </label>
              <input
                id="password"
                type="password"
                {...register('password')}
                className="w-full px-3 py-2 bg-gray-800 rounded border border-gray-700 focus:outline-none focus:border-red-custom"
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>
            
            <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                {...register('remember')}
                className="h-4 w-4 text-red-custom border-gray-700 rounded focus:ring-red-custom"
              />
              <label htmlFor="remember" className="ml-2 block text-sm">
                Ghi nhớ đăng nhập
              </label>
            </div>
            
            <div>
              <button
                type="submit"
                className="w-full bg-red-custom text-white py-2 px-4 rounded-full hover:bg-red-700 transition"
                disabled={isLoading}
              >
                {isLoading ? 'Đang xử lý...' : 'Đăng nhập'}
              </button>
            </div>
          </form>
          
          <div className="mt-4 text-center">
            <Link to="/user/forgot-password" className="text-sm text-gray-400 hover:text-white">
              Quên mật khẩu?
            </Link>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400">
              Chưa có tài khoản?{' '}
              <Link to="/user/register" className="text-red-custom hover:underline">
                Đăng ký ngay
              </Link>
            </p>
          </div>
        </div>
      </div>
   );
};

export default LoginPage;
